import React, { useEffect, useState } from 'react';
import { moderationApi } from '../../api/moderation';
import { useModerator } from '../../hooks/useModerator';
import type { EditHistoryResponse } from '../../types/moderation';
import { formatDistanceToNow } from 'date-fns';

interface ModerationHistoryProps {
  postId: string;
}

/**
 * Component to display edit history for a post.
 * Only visible to users with MODERATOR or ADMIN role.
 * Iteration 5 - Moderation System.
 */
export const ModerationHistory: React.FC<ModerationHistoryProps> = ({ postId }) => {
  const isModerator = useModerator();
  const [history, setHistory] = useState<EditHistoryResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isModerator) {
      setLoading(false);
      return;
    }
    const fetchHistory = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await moderationApi.getPostEditHistory(postId);
        setHistory(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load edit history');
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [postId, isModerator]);

  if (!isModerator) {
    return null;
  }

  if (loading) {
    return <div className="loading">Loading edit history...</div>;
  }

  if (error) {
    return <div className="error-message">Error: {error}</div>;
  }

  if (history.length === 0) {
    return <div className="no-history">No edit history available for this post.</div>;
  }

  return (
    <div className="moderation-history">
      <h3>Edit History</h3>
      <div className="history-list">
        {history.map((entry) => (
          <div key={entry.id} className="history-entry">
            <div className="history-header">
              <span className="history-editor">
                {entry.editorUsername || entry.editorId}
              </span>
              <span className="history-date">
                {formatDistanceToNow(new Date(entry.editedAt), { addSuffix: true })}
              </span>
            </div>
            {entry.editReason && (
              <div className="history-reason">
                <strong>Reason:</strong> {entry.editReason}
              </div>
            )}
            <div className="history-content">
              <div className="history-content-item">
                <strong>Previous:</strong>
                <div className="content-preview">{entry.previousContent}</div>
              </div>
              <div className="history-content-item">
                <strong>New:</strong>
                <div className="content-preview">{entry.newContent}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
